import {
  VERIFICATION_CASE_STATUSES,
  type VerificationCaseStatus,
} from '../verification-case-status/verification-case-status';

/**
 * The legal moves of the case machine — the phase's stated risk, declared once.
 *
 * Shared rather than kept in the api for the same reason as
 * `USER_STATUS_TRANSITIONS`: the staff portal renders a reviewer's available
 * actions from exactly this map, and a second hand-maintained list in the UI
 * would offer buttons the server refuses.
 *
 * Three properties the specs pin down, each load-bearing:
 *
 * - **A decision cannot be reached without passing through `in_review`.** An
 *   approval is a human act (`03-verification.md`), so there is no edge that
 *   lets a submission become a verdict — not even an administrative one. The
 *   advisory face-match therefore has no reachable path to a decision *by
 *   construction*, which is a stronger guarantee than a code review of P13b.
 * - **`draft` is unreachable.** It is what creation writes and nothing else; an
 *   edge back to it would mean losing the record that evidence was ever sent.
 * - **`approved` and `rejected` are terminal.** Re-deciding a case in place
 *   would overwrite a decision somebody is accountable for. A member who may try
 *   again gets a new case, and the refused one stays readable.
 */
export const VERIFICATION_CASE_TRANSITIONS: Readonly<
  Record<VerificationCaseStatus, readonly VerificationCaseStatus[]>
> = {
  draft: ['submitted'],
  submitted: ['in_review'],
  // `submitted` is the release valve: a reviewer who opened a case and cannot
  // decide it puts it back in the queue rather than leaving it latched to them.
  in_review: ['submitted', 'changes_requested', 'approved', 'rejected'],
  changes_requested: ['submitted'],
  approved: [],
  rejected: [],
};

/** Whether the machine permits this move. A no-op move is not a transition. */
export function canTransitionCase(
  from: VerificationCaseStatus,
  to: VerificationCaseStatus,
): boolean {
  return VERIFICATION_CASE_TRANSITIONS[from].includes(to);
}

/** The statuses reachable from here — what the review UI turns into actions. */
export function allowedCaseTransitions(
  from: VerificationCaseStatus,
): readonly VerificationCaseStatus[] {
  return VERIFICATION_CASE_TRANSITIONS[from];
}

/** Every status the machine knows, for exhaustive iteration in specs and seeds. */
export const ALL_VERIFICATION_CASE_STATUSES: readonly VerificationCaseStatus[] =
  VERIFICATION_CASE_STATUSES;
